/**
 * Auth API client — wraps the backend `/auth/*` endpoints (login and password
 * change). These functions return the full response envelope; token storage and
 * the auth context live elsewhere (this module only performs the HTTP calls).
 */
import { apiClient } from './client';

/** Credentials for {@link login}. `emailOrPhone` accepts either an email or a phone number. */
export interface LoginDto {
  emailOrPhone: string;
  password: string;
}

/**
 * Login response envelope: the JWT `access_token` (30m) + `refresh_token` (7d)
 * plus the authenticated user's profile (bilingual name, role, status).
 */
export interface LoginResponse {
  success: boolean;
  data: {
    access_token: string;
    refresh_token: string;
    user: {
      id: string;
      email: string;
      name_ar: string;
      name_en: string;
      role: string;
      status: string;
    };
  };
}

/**
 * POST `/auth/login` — authenticate with email/phone + password.
 * @param dto the {@link LoginDto} credentials.
 * @returns the full {@link LoginResponse} (tokens + user).
 */
export async function login(dto: LoginDto): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/login', dto);
  return data;
}

/** Request body for {@link changePassword}: the user's current and desired new password. */
export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
}

/**
 * POST `/auth/change-password` — change the logged-in user's password.
 * @param dto the {@link ChangePasswordDto} payload.
 * @returns `{ success, message }` from the backend.
 */
export async function changePassword(dto: ChangePasswordDto): Promise<{ success: boolean; message: string }> {
  const { data } = await apiClient.post<{ success: boolean; message: string }>('/auth/change-password', dto);
  return data;
}

